import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import { i18n } from "@/i18n";

import { useAreaStore } from "@editor/items/area/areas.store";

import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { useSceneStore } from "../scene/scenes.store";
import { isSceneElementNode } from "../sceneElement/sceneElementFunctions";
import type { TemplateItem } from "./TemplateItem";
import type { Uid } from "@/helpers/utility/Id";

export const templates: Record<string, TemplateItem> = {
  0: {
    type: "template",
    createdAt: -1,
    id: 0,
    uid: "template_0",
    title: i18n.t("templates.bsc12Goals"),
    targetPositions: {
      0: {
        id: 0,
        x: 0,
        y: 250,
        areaId: 0
      },
      1: {
        id: 1,
        x: 130,
        y: 130,
        areaId: 0
      },
      2: {
        id: 2,
        x: 270,
        y: 10,
        areaId: 0
      },
      3: {
        id: 3,
        x: 530,
        y: 10,
        areaId: 1
      },
      4: {
        id: 4,
        x: 670,
        y: 130,
        areaId: 1
      },
      5: {
        id: 5,
        x: 800,
        y: 250,
        areaId: 1
      },
      9: {
        id: 9,
        x: 270,
        y: 600,
        areaId: 2
      },
      10: {
        id: 10,
        x: 130,
        y: 480,
        areaId: 2
      },
      11: {
        id: 11,
        x: 0,
        y: 370,
        areaId: 2
      },
      6: {
        id: 6,
        x: 800,
        y: 370,
        areaId: 3
      },
      7: {
        id: 7,
        x: 670,
        y: 480,
        areaId: 3
      },
      8: {
        id: 8,
        x: 530,
        y: 600,
        areaId: 3
      }
    },
    areas: [
      {
        id: 0,
        title: i18n.t("templates.competences"),
        x: -33,
        y: -15,
        width: 530,
        height: 362,
        docs: {}
      },
      {
        id: 1,
        title: i18n.t("templates.processes"),
        x: 503,
        y: -15,
        width: 530,
        height: 362,
        docs: {}
      },
      {
        id: 2,
        title: i18n.t("templates.potentials"),
        x: -33,
        y: 353,
        width: 530,
        height: 362,
        docs: {}
      },
      {
        id: 3,
        title: i18n.t("templates.benefits"),
        x: 503,
        y: 353,
        width: 530,
        height: 362,
        docs: {}
      }
    ],
    zoom: 0.9
  },
  1: {
    type: "template",
    createdAt: -1,
    id: 1,
    uid: "template_1",
    title: i18n.t("templates.bsc12GoalsWithImages"),
    targetPositions: {
      0: {
        id: 0,
        x: -270,
        y: 20,
        areaId: 0
      },
      1: {
        id: 1,
        x: -10,
        y: -80,
        areaId: 0
      },
      2: {
        id: 2,
        x: 270,
        y: -170,
        areaId: 0
      },
      3: {
        id: 3,
        x: 530,
        y: -170,
        areaId: 1
      },
      4: {
        id: 4,
        x: 800,
        y: -80,
        areaId: 1
      },
      5: {
        id: 5,
        x: 1070,
        y: 30,
        areaId: 1
      },
      6: {
        id: 9,
        x: 270,
        y: 570,
        areaId: 2
      },
      7: {
        id: 10,
        x: -10,
        y: 470,
        areaId: 2
      },
      8: {
        id: 11,
        x: -270,
        y: 370,
        areaId: 2
      },
      9: {
        id: 6,
        x: 1070,
        y: 370,
        areaId: 3
      },
      10: {
        id: 7,
        x: 800,
        y: 470,
        areaId: 3
      },
      11: {
        id: 8,
        x: 530,
        y: 570,
        areaId: 3
      }
    },
    areas: [
      {
        id: 0,
        title: i18n.t("templates.competences"),
        x: -293,
        y: -195,
        width: 790,
        height: 542
      },
      {
        id: 1,
        title: i18n.t("templates.processes"),
        x: 503,
        y: -195,
        width: 790,
        height: 542
      },
      {
        id: 2,
        title: i18n.t("templates.potentials"),
        x: -293,
        y: 353,
        width: 790,
        height: 542
      },
      {
        id: 3,
        title: i18n.t("templates.benefits"),
        x: 503,
        y: 353,
        width: 790,
        height: 542
      }
    ],
    zoom: 0.6
  }
};

const itemStructure: TemplateItem = {
  type: "template",
  id: 0,
  uid: "template_0",

  title: "",
  targetPositions: {},
  areas: [],
  zoom: 1.0,

  docs: {},
  createdAt: -1
};
export const useTemplateStore = defineItemStore(
  "model/templates",
  itemStructure,
  {
    state: () => ({
      docs: {} as Record<string, string>
    }),

    getters: {
      templates() {
        return templates;
      },

      template(): TemplateItem | null {
        const activeScene = useSceneStore().activeScene;
        if (!activeScene) return null;
        const templateId = activeScene.templateId;
        if (templateId == null) return null;
        return templates?.[templateId] ?? null;
      },
      templateElementCount(): number {
        const template = this.template;
        if (template == null) return Infinity;
        return Object.values(template.targetPositions).length;
      },
      elementLimit(): number {
        if (this.template) {
          return this.templateElementCount;
        } else {
          return Infinity;
        }
      },

      /**
       * get all elementPositions that are not occupied by an element
       */
      freeElementPositions(): {
        x: number;
        y: number;
        id?: number;
        areaId?: number | null;
      }[] {
        const template = this.template;
        if (!template) {
          return [];
        }

        // loop through the positions and see if the position is taken.
        // return the ones not taken.
        const elementPositions = Object.values(template.targetPositions);
        const takenPositionIds = this.takenPositionIds;
        return elementPositions.filter(
          (position) => !takenPositionIds.includes(position.id)
        );
      },

      positionIds(): number[] {
        const template = this.template;
        if (!template) return [];

        const elementPositions = Object.values(template.targetPositions);
        return elementPositions.map((p) => p.id);
      },

      /**
       * returns the targetId that is placed at each position:
       */
      positionElementUids(): {
        [positionId: number]: Uid<"target" | "resource" | "index">;
      } {
        const template = this.template;
        if (template == null) return {};

        const activeScene = useSceneStore().activeScene;
        if (!activeScene) return {};

        const positions = Object.values(template.targetPositions);

        const positionPlaces: { [positionId: number]: Uid<"target" | "resource" | "index"> } = {};

        const elementNodes = useSceneNodesStore().orderedItems.filter((node) => node.sceneId === activeScene.id).filter(isSceneElementNode);

        // check all elements
        for (const node of elementNodes) {
          // check if the element is exactly on one elementPosition
          const position = positions.find(
            (position) => node.x === position.x && node.y === position.y
          );

          // if the element is on a position, save the element id in that position.
          if (position != null) {
            positionPlaces[position.id] = node.data.elementUid;
          }
        }

        return positionPlaces;
      },

      /**
       * returns the position ids that are taken by elements
       */
      takenPositionIds(): number[] {
        return Object.keys(this.positionElementUids).map((positionId) =>
          parseInt(positionId)
        );
      }
    },

    actions: {
      /**
       * Use one of the provided templates.
       */
      async activate(templateId = 0) {
        // delete the current states by deactivating the current net.
        await this.deactivate();

        const sceneStore = useSceneStore();
        const activeScene = sceneStore.activeScene;
        if (!activeScene) return;

        sceneStore.update({
          id: activeScene.id,
          templateId: templateId
        });

        console.log("new template", this.template, templateId, templates);

        // if there's a template, create its areas.
        if (this.template !== null) {
          if (this.template.zoom) {
            sceneStore.update({
              id: activeScene.id,
              translateX: 0,
              translateY: 0,
              scale: this.template.zoom
            });
          }

          const areaStore = useAreaStore();
          for (const area of this.template.areas) {
            await areaStore.create({ ...area, sceneId: activeScene.id });
          }
          this.realignPlaceholders();
        }
      },

      async deactivate() {
        const sceneStore = useSceneStore();
        const activeScene = sceneStore.activeScene;
        const activeTemplate = this.template;

        if (!activeScene) return;
        if (!activeTemplate) return;

        // delete all areas
        const areaStore = useAreaStore();
        const sceneAreas = areaStore.orderedItems.filter(area => area.sceneId === activeScene.id);
        await areaStore.deleteMany(sceneAreas);

        sceneStore.update({
          id: activeScene.id,
          templateId: null
        });
      },
      realignPlaceholders() {
        const template = this.template;
        if (template == null) return;
        const positions = template.targetPositions ?? {};
        for (const index in positions) {
          const position = positions[index];
          position.x = Math.round(position.x / 10) * 10;
          position.y = Math.round(position.y / 10) * 10;
        }
      },
      set(options: Record<string, unknown>) {
        this.$patch((state) => {
          Object.assign(state, options);
        });
      }
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useTemplateStore, import.meta.hot));
}
